import { getGoogleAccessToken, supabase } from '@/app/lib/supabase';
import { ScheduledCourse } from '@/app/types/course';
import { GOOGLE_CALENDAR_COLORS } from '@/app/types/colors';
import { SEMESTER_BY_ID, normalizeSemesterId } from '@/app/lib/semesters';

const getSemesterConfig = (termId?: string) => {
  return {
    ...SEMESTER_BY_ID[normalizeSemesterId(termId)],
    timeZone: 'America/New_York',
  };
};

// Day mapping for RRULE
const DAY_MAPPING: Record<string, string> = {
  'M': 'MO',
  'T': 'TU', 
  'W': 'WE',
  'TH': 'TH',
  'F': 'FR',
  'Monday': 'MO',
  'Tuesday': 'TU',
  'Wednesday': 'WE',
  'Thursday': 'TH',
  'Friday': 'FR',
  'Saturday': 'SA',
  'Sunday': 'SU'
};

interface GoogleCalendarEvent {
  summary: string;
  description: string;
  location: string;
  start: {
    dateTime: string;
    timeZone: string;
  };
  end: {
    dateTime: string;
    timeZone: string;
  };
  recurrence?: string[];
  colorId?: string; // For campus color coding
}

class GoogleCalendarService {
  private static instance: GoogleCalendarService;
  private accessToken: string | null = null;
  private scarletSyncCalendarId: string | null = null;

  private constructor() {}

  static getInstance(): GoogleCalendarService {
    if (!GoogleCalendarService.instance) {
      GoogleCalendarService.instance = new GoogleCalendarService();
    }
    return GoogleCalendarService.instance;
  }

  private async getAccessToken(): Promise<string> {
    // Prefer stored token so access persists across sessions
    if (!this.accessToken) {
      try {
        const { data, error } = await supabase.functions.invoke('users', {
          body: { action: 'get_google_token' }
        });
        if (!error && data?.access_token) {
          const expiresAt = data?.expires_at ? new Date(data.expires_at) : null;
          const isExpired = expiresAt ? expiresAt.getTime() - Date.now() < 60_000 : false;
          if (!isExpired) {
            this.accessToken = data.access_token as string;
            return this.accessToken;
          }
        }
      } catch (_) {
        // fallback below
      }
      // Fallback to current provider token (valid during fresh login)
      this.accessToken = await getGoogleAccessToken();
      // Best effort: persist it for future use
      try {
        await supabase.functions.invoke('users', {
          body: {
            action: 'save_google_token',
            access_token: this.accessToken,
            // If session has expiry we could pass it; keep undefined if not known
          }
        });
      } catch (_) {
        // ignore persistence failure
      }
    }
    return this.accessToken;
  }

  private async createScarletSyncCalendar(scheduleName?: string): Promise<string> {
    const accessToken = await this.getAccessToken();
    
    const calendarName = scheduleName ? `${scheduleName} - Scarlet Sync` : 'Scarlet Sync';
    
    const calendarData = {
      summary: calendarName,
      description: 'Course schedule exported from Scarlet Sync',
      timeZone: 'America/New_York'
    };

    const response = await fetch('https://www.googleapis.com/calendar/v3/calendars', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(calendarData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(`Failed to create calendar: ${error.error?.message || 'Unknown error'}`);
    }

    const calendar = await response.json();
    return calendar.id || '';
  }

  private async getOrCreateScarletSyncCalendar(scheduleName?: string): Promise<string> {
    if (this.scarletSyncCalendarId) {
      return this.scarletSyncCalendarId;
    }

    // Try to find existing Scarlet Sync calendar
    const accessToken = await this.getAccessToken();
    const response = await fetch('https://www.googleapis.com/calendar/v3/users/me/calendarList', {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    });

    if (response.ok) {
      const calendars = await response.json();
      const scarletSyncCalendar = calendars.items?.find(
        (cal: any) => cal.summary === 'Scarlet Sync' || cal.summary?.includes('Scarlet Sync')
      );
      
      if (scarletSyncCalendar?.id) {
        this.scarletSyncCalendarId = scarletSyncCalendar.id;
        return scarletSyncCalendar.id;
      }
    }

    // Create new Scarlet Sync calendar
    const newCalendarId = await this.createScarletSyncCalendar(scheduleName);
    this.scarletSyncCalendarId = newCalendarId;
    return newCalendarId;
  }

  private convertTimeToMinutes(time: string): number {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  }

  private formatTimeForGoogleCalendar(minutes: number, date: string): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const timeString = `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:00`;
    return `${date}T${timeString}-04:00`; // EDT timezone
  }

  private generateRRULE(meetingTimes: any[], startDate: string, endDate: string): string[] {
    const recurrenceRules: string[] = [];
    
    meetingTimes.forEach(meeting => {
      if (!meeting.day || !meeting.startMinute || !meeting.endMinute) return;
      
      const dayCode = DAY_MAPPING[meeting.day];
      if (!dayCode) return;
      
      // Create RRULE for weekly recurrence until semester end
      const rrule = `RRULE:FREQ=WEEKLY;BYDAY=${dayCode};UNTIL=${endDate.replace(/-/g, '')}T235959Z`;
      recurrenceRules.push(rrule);
    });
    
    return recurrenceRules;
  }

  private calculateStartDateForDay(day: string, semesterStartDate: string): string {
    // Convert semester start date to Date object
    const semesterStart = new Date(semesterStartDate);
    
    // Day mapping for Date.getDay() (0 = Sunday, 1 = Monday, etc.)
    const dayToDateMap: Record<string, number> = {
      'M': 1,      // Monday
      'T': 2,      // Tuesday
      'W': 3,      // Wednesday
      'TH': 4,     // Thursday
      'F': 5,      // Friday
      "Monday": 1,
      "Tuesday": 2,
      "Wednesday": 3,
      "Thursday": 4,
      "Friday": 5,
      "Saturday": 6,
      "Sunday": 0
    };
    
    const targetDay = dayToDateMap[day];
    if (targetDay === undefined) {
      return semesterStartDate; // Fallback to semester start date
    }
    
    // Find the next occurrence of this day of the week after semester start
    const currentDay = semesterStart.getDay();
    
    // Calculate days to add to get to the target day
    let daysToAdd = targetDay - currentDay;
    if (daysToAdd <= 0) {
      daysToAdd += 7; // If the target day is today or earlier, go to next week
    }
    
    const firstOccurrence = new Date(semesterStart);
    firstOccurrence.setDate(semesterStart.getDate() + daysToAdd);
    
    // Debug logging for Monday classes
    if (day === 'M' || day === 'Monday') {
      console.groupCollapsed('GoogleCalendar: Monday start calc');
      console.log('target day:', targetDay, 'current day:', currentDay, 'daysToAdd:', daysToAdd);
      console.log('semester start:', semesterStartDate, 'first occurrence:', firstOccurrence.toISOString().split('T')[0]);
      console.groupEnd();
    }
    
    // Format as YYYY-MM-DD
    return firstOccurrence.toISOString().split('T')[0];
  }

  private createEventFromMeetingTime(course: ScheduledCourse, meetingTime: any, semesterStartDate: string): GoogleCalendarEvent {
    // Calculate the correct start date for this specific day of the week
    const eventStartDate = this.calculateStartDateForDay(meetingTime.day, semesterStartDate);
    
    const startTime = this.formatTimeForGoogleCalendar(meetingTime.startMinute, eventStartDate);
    const endTime = this.formatTimeForGoogleCalendar(meetingTime.endMinute, eventStartDate);

    const description = [
      `Course: ${course.name}`,
      `Section: ${course.section}`,
      `Instructors: ${course.instructors.join(', ') || 'TBA'}`,
      `Credits: ${course.credits}`,
      `Campus: ${course.campus}`,
      `Department: ${course.department}`,
      `Class Number: ${course.class_number}`,
      // link to the explore page
      `Link: https://scarletsync.app/explore?course=${course.baseId}`
    ].join('\n');

    const event: GoogleCalendarEvent = {
      summary: `${course.name} - ${course.section}`,
      description,
      location: "Rutgers, " + course.campus + " Campus, Room " + meetingTime.location,
      start: {
        dateTime: startTime,
        timeZone: 'America/New_York'
      },
      end: {
        dateTime: endTime,
        timeZone: 'America/New_York'
      },
      colorId: GOOGLE_CALENDAR_COLORS[course.campus] || GOOGLE_CALENDAR_COLORS['Unknown']
    };

    console.groupCollapsed('GoogleCalendar: Event build');
    console.log('color for campus', course.campus, GOOGLE_CALENDAR_COLORS[course.campus]);

    // log meedting day and the rrule
    console.log('meeting day:', meetingTime.day);
    console.log('recurrence before:', event.recurrence);

    // Add recurrence rules for weekly events
    if (meetingTime.day) {
      const dayCode = DAY_MAPPING[meetingTime.day];
      if (dayCode) {
        const rrule = `RRULE:FREQ=WEEKLY;BYDAY=${dayCode};UNTIL=20260505T235959Z`;
        event.recurrence = [rrule];
        console.log('recurrence after:', event.recurrence);
        
        // Debug logging for Monday classes
        if (meetingTime.day === 'M' || meetingTime.day === 'Monday') {
          console.log('Monday RRULE debug', { day: meetingTime.day, dayCode, rrule });
        }
      }
    }
    console.log('event built');
    console.groupEnd();


    return event;
  }

  async exportScheduleToGoogleCalendar(courses: ScheduledCourse[], scheduleName?: string, termId?: string): Promise<{ success: boolean; message: string; eventCount: number }> {
    try {
      const calendarId = await this.getOrCreateScarletSyncCalendar(scheduleName);
      const accessToken = await this.getAccessToken();
      
      // Get semester configuration
      const semesterConfig = getSemesterConfig(termId);
      
      // First, clear all existing events from the Scarlet Sync calendar
      console.groupCollapsed('GoogleCalendar: Clearing existing events');
      const clearResponse = await fetch(
        `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calendarId)}/events`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (clearResponse.ok) {
        const existingEvents = await clearResponse.json();
        for (const event of existingEvents.items || []) {
          await fetch(
            `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calendarId)}/events/${event.id}`,
            {
              method: 'DELETE',
              headers: {
                'Authorization': `Bearer ${accessToken}`,
              },
            }
          );
        }
        console.log('cleared:', existingEvents.items?.length || 0);
      }
      console.groupEnd();
      
      let eventCount = 0;
      const errors: string[] = [];

      for (const course of courses) {
        // Create separate events for each meeting time
        if (!course.meetingTimes || course.meetingTimes.length === 0) {
          errors.push(`No meeting times found for ${course.name}`);
          continue;
        }

        for (const meetingTime of course.meetingTimes) {
          try {
            const event = this.createEventFromMeetingTime(course, meetingTime, semesterConfig.startDate);
            
            const response = await fetch(
              `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calendarId)}/events`,
              {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${accessToken}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify(event),
              }
            );

            if (!response.ok) {
              const error = await response.json();
              errors.push(`Failed to create event for ${course.name} (${meetingTime.day}): ${error.error?.message || 'Unknown error'}`);
            } else {
              eventCount++;
            }
          } catch (error) {
            errors.push(`Error creating event for ${course.name} (${meetingTime.day}): ${error instanceof Error ? error.message : 'Unknown error'}`);
          }
        }
      }

      if (errors.length > 0) {
        return {
          success: false,
          message: `Export completed with errors: ${errors.join('; ')}`,
          eventCount
        };
      }

      return {
        success: true,
        message: `Successfully exported ${eventCount} events to Scarlet Sync calendar`,
        eventCount
      };
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to export schedule',
        eventCount: 0
      };
    }
  }

  async checkGoogleCalendarAccess(): Promise<boolean> {
    try {
      await this.getAccessToken();
      return true;
    } catch (error) {
      return false;
    }
  }

  // New: Export as ICS link (no OAuth needed)
  async exportScheduleToICS(
    courses: ScheduledCourse[],
    scheduleName?: string,
    semester?: { startDate?: string; endDate?: string; timeZone?: string }
  ): Promise<Blob> {
    const res = await fetch('/api/export/ics', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ courses, scheduleName, semester }),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Failed to generate ICS: ${text || res.statusText}`);
    }
    return await res.blob();
  }
}

export const googleCalendarService = GoogleCalendarService.getInstance();
